/**
 * 这个页面全部签文的唯一真实来源,server 与 client 共用。
 *
 * 为什么单独成文件(和 faq-data.ts 同理):client 组件要拿它渲染列表,而
 * page.tsx(server)要拿 totalMessageCount 去拼 metadata 里的数字。数据若定义
 * 在带 "use client" 的组件里再导出,server 侧拿到的是 client reference。
 *
 * ## 分组标题是按关键词排的,不是按"什么好听"排的
 *
 * DataForSEO 实测:本页含 "messages" 的词多数已进 SERP 第一页(#5~#10),而含
 * sayings / quotes / hilarious / witty 的同义变体全部卡在 #11~#20。翻代码就能
 * 看出原因 —— 改版前的页面正文里 "sayings" 出现 0 次、"quotes" 出现 0 次,
 * 四个小标题(Self-Deprecating Humor / Absurdist Wisdom / Modern Life Humor /
 * Unexpected Twists)一个目标词都不含。title 承诺 sayings,正文一次都不说。
 *
 * 所以每个分组的 heading 都显式挂一个卡在第二页的词形,注释里标了目标词和
 * 月搜索量。改标题前请先看一眼你要动的是哪个词。
 *
 * 硬约束:heading 必须是自然英文。宁可少覆盖一个词,也不要写出
 * "Funny Fortune Cookies Sayings" 这种为塞词而拧巴的句子 —— 那是垃圾信号。
 */

export interface FunnyMessageGroup {
  /** 锚点 id,用于页内目录跳转 */
  id: string;
  /** 渲染成 <h2>。见文件头说明:每个都对应一个具体的目标词。 */
  heading: string;
  /** 小标题下的引导句。改版前整页没有任何说明性散文,竞品普遍有。 */
  blurb: string;
  /** 页内目录里的短标签,heading 太长放不下 */
  navLabel: string;
  messages: string[];
}

export const funnyMessageGroups: FunnyMessageGroup[] = [
  {
    // 目标词: funny fortune cookie sayings (1,000/月, 实测 #16)
    id: "sayings",
    heading: "Funny Fortune Cookie Sayings for Everyday Life",
    navLabel: "Everyday Sayings",
    blurb:
      "Classic proverbs, gently sabotaged. These funny fortune cookie sayings sound like wisdom right up until the last four words.",
    messages: [
      "A journey of a thousand miles begins with someone else driving.",
      "He who laughs last probably did not get the joke the first time.",
      "The early bird gets the worm. The late bird gets brunch.",
      "Patience is a virtue. Virtue is boring. Pay for the express shipping.",
      "A penny saved is a penny you forgot you had.",
      "Do not count your chickens. They find it unsettling.",
      "The grass is always greener where somebody else is watering it.",
      "Actions speak louder than words, which is why you should stop yelling.",
      "Good things come to those who wait. Better things come to those who ask.",
      "A watched pot never boils. An unwatched pot boils over. Choose your regret.",
      "When one door closes, another one is probably also locked.",
      "Rome was not built in a day, but your excuses were.",
      "Honesty is the best policy. Silence is a strong runner-up.",
      "You cannot buy happiness, but you can order it with free delivery.",
      "Two wrongs do not make a right, but three lefts do.",
    ],
  },
  {
    // 目标词: hilarious fortune cookies (1,600/月, 实测 #16) —— 复数 cookies 的词形
    id: "parties",
    heading: "Hilarious Fortune Cookies for Parties and Pranks",
    navLabel: "Parties & Pranks",
    blurb:
      "Print these hilarious fortune cookies, fold them into homemade cookies, and watch the table go quiet as everybody reads their own.",
    messages: [
      "Someone at this table is lying about how much they liked the food.",
      "You will be the last one to leave tonight. Everybody already knows it.",
      "The person on your left owes you a drink. They know why.",
      "You will make a toast tonight. It will go on far too long.",
      "Tonight you will dance like nobody is watching. Everybody is watching.",
      "Congratulations, you got the good cookie. Do not tell the others.",
      "Your karaoke song choice will be discussed for years.",
      "You will be tagged in a photo you did not consent to.",
      "One person here has been mispronouncing your name all evening.",
      "You will offer to host next time. You will regret it by Tuesday.",
      "The host is watching to see if you take seconds. Take thirds.",
      "Your plus-one is having a better time than you are.",
      "You will start a group chat tonight that outlives this friendship.",
      "There is one slice left. It is not for you.",
      "You will say we should do this more often and mean it for ten minutes.",
    ],
  },
  {
    // 目标词: funny fortune cookies quotes (1,000/月, 实测 #20)
    id: "work",
    heading: "Funny Fortune Cookie Quotes About Work and Money",
    navLabel: "Work & Money",
    blurb:
      "Funny fortune cookie quotes for anybody who has ever sat through a meeting that could have been an email. Slip one into a coworker's lunch.",
    messages: [
      "A promotion is coming. So are the responsibilities you did not ask for.",
      "That meeting could have been an email. So could the next four.",
      "Your inbox will reach zero. Briefly. Enjoy it.",
      "You will be praised for work you finished at 4:58 in the afternoon.",
      "Someone will say let us circle back. You may leave the circle.",
      "Money cannot buy happiness, but it does cover the shipping.",
      "Your budget is a work of fiction. A bestseller, but fiction.",
      "You will open a savings account. You will visit it once.",
      "The printer knows what you did. The printer will not forget.",
      "Your side project will make money the week you stop needing it to.",
      "You will reply sounds good to something that does not sound good.",
      "A raise is in your future. So is inflation. They have met before.",
      "Your best idea will arrive during somebody else's presentation.",
      "You will be the only person who read the entire document.",
      "Working from home means a short commute and a much closer refrigerator.",
    ],
  },
  {
    // 目标词: witty fortunes (720/月, 实测 #18)
    id: "witty",
    heading: "Witty Fortunes with a Sharp Edge",
    navLabel: "Witty & Sharp",
    blurb:
      "Witty fortunes for people who like their humor with a little sting. Accurate enough to be uncomfortable, short enough to fit in a cookie.",
    messages: [
      "You are not lost. You are gathering data.",
      "Your standards are fine. Your sample size is not.",
      "You have excellent taste and a terrible budget.",
      "Being right is easy. Being right out loud is expensive.",
      "You are the main character. So is everybody else. Awkward.",
      "Your gut is correct about sixty percent of the time. Choose carefully.",
      "You are one good night of sleep away from a completely different opinion.",
      "Confidence is quiet. So is having nothing to say. Learn the difference.",
      "You will win an argument today and lose the room.",
      "Nobody is thinking about it as much as you are. Nobody.",
      "You are not behind. You are comparing your draft to somebody else's edit.",
      "The advice you keep giving is the advice you keep ignoring.",
      "You do not need a sign. You need a decision.",
      "Your comfort zone has excellent wifi and no exits.",
      "You will be misunderstood by somebody who stopped reading at line one.",
    ],
  },
  {
    // 目标词: funny cookie fortunes (1,600/月, 实测 #20) —— 注意是 cookie fortunes 语序
    id: "food",
    heading: "Funny Cookie Fortunes About Food and Takeout",
    navLabel: "Food & Takeout",
    blurb:
      "Funny cookie fortunes about the meal you just finished and the one you are already thinking about. Best served with the check.",
    messages: [
      "You will be hungry again within the hour. This is a promise, not a prediction.",
      "The leftovers are gone. You know exactly who took them.",
      "You will order for two. You are one person. This is fine.",
      "Your refrigerator contains three condiments and a dream.",
      "You will announce that you are full and then discover dessert.",
      "The last dumpling is technically communal. Technically.",
      "You will burn your mouth on the first bite. Every time. Forever.",
      "Your grocery list and your grocery cart have never actually met.",
      "Somewhere in your kitchen is a vegetable that has given up.",
      "You will try to recreate this meal at home. It will not be the same.",
      "The delivery driver has seen things. Tip accordingly.",
      "Snacks eaten standing at the counter contain no calories. This is science.",
      "You will finish the bag. Do not pretend otherwise.",
      "Your takeout order says more about you than your resume does.",
      "There is a container in your refrigerator you are afraid to open. It knows.",
    ],
  },
  {
    // 目标词: funny fortune (720/月, 实测 #14)
    // 原页面的 Self-Deprecating Humor 四条全部保留在这里
    id: "self-deprecating",
    heading: "Self-Deprecating Funny Fortunes",
    navLabel: "Self-Deprecating",
    blurb:
      "A funny fortune lands hardest when it is about you. These are for anybody whose to-do list has a to-do list.",
    messages: [
      "You will find what you are looking for right after you stop looking for it.",
      "Your patience will be tested. Mainly by you.",
      "You are destined for greatness. Just not today. Maybe tomorrow.",
      "The fortune you seek is in another cookie.",
      "You will make a plan tonight and betray it by morning.",
      "Your to-do list has three items and one of them is make a to-do list.",
      "You are doing your best. Your best is having an off week.",
      "You will start something new before finishing something old.",
      "Today you will win a small victory and immediately downplay it.",
      "Your alarm is set. Your intentions are not.",
      "You will remember the thing you forgot at the worst possible moment.",
      "You are extremely organized in exactly one area of your life.",
      "You will apologize for something that was not your fault. Again.",
      "Great things take time. You have been taking a great deal of time.",
      "You will get around to it. It will not be around.",
    ],
  },
  {
    // 目标词: hilarious fortune cookie sayings (1,000/月, 实测 #16)
    // 原页面的 Absurdist Wisdom 四条全部保留在这里
    id: "absurdist",
    heading: "Absurdist and Hilarious Fortune Cookie Sayings",
    navLabel: "Absurdist",
    blurb:
      "Hilarious fortune cookie sayings that refuse to explain themselves. Read one out loud and let the silence do the work.",
    messages: [
      "A duck's quack does not echo. Neither does your advice.",
      "You will step on a Lego. This is unavoidable.",
      "Today is a good day to wear mismatched socks on purpose.",
      "Your spirit animal is a confused penguin.",
      "Somewhere a pigeon is thinking about you. It is not a kind thought.",
      "The moon owes you nothing, and yet.",
      "Beware of a man with two forks and no plan.",
      "Your left shoe has been carrying this team for years.",
      "A stranger will nod at you. Nod back. The pact is sealed.",
      "The soup remembers.",
      "You will be outsmarted by a door marked PUSH.",
      "In another timeline you are a lighthouse. You are doing fine there.",
      "Trust the bees. Question the wasps.",
      "Your houseplant has opinions about your schedule.",
      "Do not argue with a goose. You will not win and it will not stop.",
    ],
  },
  {
    // 目标词: funny fortune cookie messages (480/月, 实测 #11) —— 本页最接近首页的词
    // 原页面的 Modern Life Humor 四条全部保留在这里
    id: "modern-life",
    heading: "Funny Fortune Cookie Messages About Modern Life",
    navLabel: "Modern Life",
    blurb:
      "Funny fortune cookie messages for a century the cookie was not designed for. Battery anxiety, group chats, and forty-seven open tabs.",
    messages: [
      "Your wifi signal will be strong today. Your willpower, not so much.",
      "You will remember that embarrassing thing you did in 2012. Again.",
      "Your inbox is almost clean. Almost.",
      "Someone will ask if you are working hard or hardly working. Prepare yourself.",
      "Your phone is at four percent. Your charger is in the other room. Choose.",
      "You will read the terms and conditions. No you will not.",
      "An app you deleted will find its way back onto your phone.",
      "You will watch a forty-minute video to avoid a four-minute task.",
      "Your screen time report is a personal attack and it is correct.",
      "You will type a long reply, delete it, and send a thumbs up.",
      "There are forty-seven tabs open. Three of them matter. You will never find them.",
      "You will change your password to the old password with a two on the end.",
      "Somebody will send you a voice message. It will be four minutes long.",
      "Your smart home is smarter than you are and it is getting bored.",
      "You will pause the show to look up an actor and never resume the show.",
    ],
  },
  {
    // 目标词: funny fortune cookies quotes 的第二个落点(元幽默是本站差异化内容)
    // 原页面的 Unexpected Twists 四条全部保留在这里
    id: "meta",
    heading: "Meta Fortune Cookie Quotes That Break the Fourth Wall",
    navLabel: "Meta & Twists",
    blurb:
      "Fortune cookie quotes about being a fortune cookie. The paper knows what it is, and it has made peace with that.",
    messages: [
      "Help! I am trapped in a fortune cookie factory!",
      "This fortune intentionally left blank for contemplation.",
      "Error 404: fortune not found. Try refreshing your karma.",
      "You opened the wrong cookie. This one was meant for somebody cooler.",
      "Disregard the previous fortune. It was only a draft.",
      "This message will self-destruct. Eventually. Everything does.",
      "The cookie was the fortune all along.",
      "Congratulations. You have read further than most people do.",
      "You were supposed to get a lucky number. There were budget cuts.",
      "This fortune has been recycled. Please do not tell the last person.",
      "Your future is currently buffering.",
      "A second opinion is available in the next cookie. It disagrees.",
      "This fortune is legally required to remain vague.",
      "Somebody wrote this. Sit with that for a moment.",
      "You have reached the end of this fortune. There is nothing else here.",
    ],
  },
  {
    // 兜底分组:love/friendship 本身没有卡在第二页的目标词,但竞品(virtualedge)
    // 都有这一类,缺了会显得清单不完整。
    id: "love",
    heading: "Funny Fortunes About Love and Friendship",
    navLabel: "Love & Friends",
    blurb:
      "Funny fortunes for the people who put up with you. Warmer than the rest of this page, but only slightly.",
    messages: [
      "You will find happiness with a new love. It may have four legs.",
      "Someone is typing. They have been typing for quite a while now.",
      "Your friend will ask for advice and then do the opposite. Let them.",
      "You will fall for somebody who texts in complete sentences. Treasure it.",
      "The person who always says let us get coffee means it this time. Probably.",
      "You will be in a wedding party before you feel ready for it.",
      "Love is patient. Love is kind. Love is also terrible at picking restaurants.",
      "Your oldest friend still tells that story wrong on purpose.",
      "You will meet somebody new. They will already know your entire family.",
      "A great romance begins when somebody stops trying to be interesting.",
      "You will send a message to the wrong chat. It will work out. Somehow.",
      "The best friendships survive bad haircuts and worse decisions.",
      "You will be somebody's emergency contact and never find out.",
      "Somebody remembers your coffee order. That is the whole fortune.",
      "You will forgive them before they apologize. Superpower and problem, both.",
    ],
  },
];

/**
 * 页面上真实渲染的签文总数。
 *
 * metadata 的 title / description 和 H1 里的数字都必须从这里取,不许手写。
 * 历史教训:标题曾经写着「100+」而页面上只有 16 条 —— 差 6 倍的承诺-兑现落差,
 * 既误导用户,也正是 helpful content 系统盯的信号,最后只能把数字整个删掉。
 * 现在数字和数组绑死,加一条减一条标题自动跟着变,并有测试守卫(见
 * __tests__/app/funny-messages-content.test.ts)。
 */
export const totalMessageCount = funnyMessageGroups.reduce(
  (sum, group) => sum + group.messages.length,
  0,
);
